import { Add, Bin, Breakpoints, CTA, Divider, Edit, TextAppearance, TextColor, TextCustom, TextLineHeight, TextSize, TextType, TextWeight, useIsMobile } from "@vw-marketing/us-components";
import React from "react";
import { StyledManageContactsAlerts, StyledManageContactsAlertsAdditional, StyledManageContactsAlertsAdditionalContent, StyledManageContactsAlertsAdditionalCTA, StyledManageContactsAlertsAdditionalCTAEdit, StyledManageContactsAlertsAdditionalCTARemove, StyledManageContactsAlertsContent, StyledManageContactsAlertsPrimary, StyledManageContactsAlertsTitle } from "./styles";
import { applyTextTransformations } from "../../../../shared/utils/applyTextTransformations";

interface ManageContactsAlertsProps {
    title: string;
    primaryAlertText: string,
    additionalAlertText: string,
    addCTAText: string,
    primaryContent: string,
    additionalContent: string[],
    handleAddEdit: (flow: 'add' | 'edit', initialValue?: string) => void;
    handleRemove: (contentToRemove: string) => void;
    translationHandler: (key: string) => string;
}

export const ManageContactsAlerts: React.FC<ManageContactsAlertsProps> = ({
    title,
    primaryAlertText,
    addCTAText,
    additionalAlertText,
    primaryContent,
    additionalContent,
    handleAddEdit,
    handleRemove, 
    translationHandler
}) => {
    const isMobile = useIsMobile(Breakpoints.b1280);
    const isSmallMobile = useIsMobile(370);

    return (
        <>
            <StyledManageContactsAlerts>
                <StyledManageContactsAlertsTitle>
                    <TextCustom
                        appearance={TextAppearance.copy200}
                        style={{
                            [Breakpoints.default]: {
                                color: TextColor.inherit,
                                fontSize: TextSize.TextSize20,
                                fontType: TextType.Copy,
                                fontWeight: TextWeight.bold,
                                lineHeight: TextLineHeight.TextLineHeight32,
                            },
                            [Breakpoints.b1280]: {
                                fontSize: TextSize.TextSize24,
                                lineHeight: TextLineHeight.TextLineHeight36,
                            },
                        }}
                    >
                        {title}
                    </TextCustom>
                    {additionalContent.length < 2 && 
                    <CTA
                        tag="button"
                        emphasis="tertiary"
                        icon={<Add variant="small"/>}
                        onClick={() => handleAddEdit('add')}
                    >
                        {addCTAText}
                    </CTA>}
                </StyledManageContactsAlertsTitle>
                <StyledManageContactsAlertsContent>
                    <StyledManageContactsAlertsPrimary>
                        <TextCustom
                            appearance={TextAppearance.copy200}
                            style={{
                                [Breakpoints.default]: {
                                    color: TextColor.inherit,
                                    fontSize: TextSize.TextSize14,
                                    fontType: TextType.Copy,
                                    fontWeight: TextWeight.bold,
                                    lineHeight: TextLineHeight.TextLineHeight24,
                                },
                                [Breakpoints.b1280]: {
                                    fontSize: TextSize.TextSize16
                                },
                            }}
                        >
                            {primaryAlertText}
                        </TextCustom>
                        <TextCustom
                            appearance={TextAppearance.copy200}
                            style={{
                                [Breakpoints.default]: {
                                    color: TextColor.inherit,
                                    fontSize: TextSize.TextSize14,
                                    fontType: TextType.Copy,
                                    lineHeight: TextLineHeight.TextLineHeight24,
                                },
                                [Breakpoints.b1280]: {
                                    fontSize: TextSize.TextSize16
                                },
                            }}
                        >
                            {applyTextTransformations(primaryContent, [primaryContent])}
                        </TextCustom>
                    </StyledManageContactsAlertsPrimary>
                    {additionalContent.length > 0 && additionalContent.map((content) => (
                        <>
                            <Divider />
                            <StyledManageContactsAlertsAdditional>
                                <StyledManageContactsAlertsAdditionalContent>
                                    <TextCustom
                                        appearance={TextAppearance.copy200}
                                        style={{
                                            [Breakpoints.default]: {
                                                color: TextColor.inherit,
                                                fontSize: TextSize.TextSize14,
                                                fontType: TextType.Copy,
                                                fontWeight: TextWeight.bold,
                                                lineHeight: TextLineHeight.TextLineHeight24,
                                            },
                                            [Breakpoints.b1280]: {
                                                fontSize: TextSize.TextSize16
                                            },
                                        }}
                                    >
                                        {additionalAlertText}
                                    </TextCustom>
                                    <TextCustom
                                        appearance={TextAppearance.copy200}
                                        style={{
                                            [Breakpoints.default]: {
                                                color: TextColor.inherit,
                                                fontSize: TextSize.TextSize14,
                                                fontType: TextType.Copy,
                                                lineHeight: TextLineHeight.TextLineHeight24,
                                            },
                                            [Breakpoints.b1280]: {
                                                fontSize: TextSize.TextSize16
                                            },
                                        }}
                                    >
                                        {applyTextTransformations(content, [content])}
                                    </TextCustom>
                                </StyledManageContactsAlertsAdditionalContent>
                                <StyledManageContactsAlertsAdditionalCTA>
                                    <StyledManageContactsAlertsAdditionalCTARemove
                                        tag="button"
                                        emphasis="secondary"
                                        size={"small"}
                                        stretchContent={isSmallMobile}
                                        icon={<Bin variant="small" />}
                                        onClick={() => handleRemove(content)}
                                        customProperties={isSmallMobile || !isMobile}
                                    >
                                        {(isSmallMobile || !isMobile) && translationHandler("accountSettings.manage.contacts.interaction.layer.alerts.cta.remove")}
                                    </StyledManageContactsAlertsAdditionalCTARemove>
                                    <StyledManageContactsAlertsAdditionalCTAEdit
                                        tag="button"
                                        emphasis="secondary"
                                        size={"small"}
                                        stretchContent={isSmallMobile}
                                        icon={<Edit variant="small" />}
                                        onClick={() => handleAddEdit('edit', content)}
                                        customProperties={isSmallMobile || !isMobile}
                                    >
                                        {(isSmallMobile || !isMobile) && translationHandler("accountSettings.manage.contacts.interaction.layer.alerts.cta.edit")}
                                    </StyledManageContactsAlertsAdditionalCTAEdit>
                                </StyledManageContactsAlertsAdditionalCTA>
                            </StyledManageContactsAlertsAdditional>
                        </>
                    ))}
                    <Divider />
                </StyledManageContactsAlertsContent>
            </StyledManageContactsAlerts>
        </>);
}